// Copyright © 2021 Move Closer

import Glide from '@glidejs/glide'
import { Component, Ref } from 'vue-property-decorator'

import { AbstractModuleUi } from '../_abstract/ui'
import { ModuleHeaderUi } from '../_components/ui'
import { RefType } from '../_contracts'

import { TestimonialsModule } from './Testimonials.contracts'

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
@Component<TestimonialsModuleUi>({
  name: 'TestimonialsModuleUi',
  components: { ModuleHeaderUi },

  mounted (): void {
    this.readNavbarHeight()
    this.initGlide()
  },

  template: `
    <section class="testimonials-module" :style="{ background }">
      <div class="container">
        <div class="row justify-content-center">

          <!-- Heading -->
          <div class="col-12">
            <ModuleHeaderUi v-if="module.content.header" :header="module.content.header" />
          </div>

          <!-- Testimonials -->
          <div class="col-12 col-lg-8">
            <div v-if="module.content.testimonials && module.content.testimonials.length"
                 class="testimonials-module__slider glide">
              <!-- Slides -->
              <div class="glide__track" data-glide-el="track" ref="glideTrackRef">
                <ul class="glide__slides list-unstyled">
                  <li v-for="testimonial in module.content.testimonials" :key="testimonial.text"
                      class="glide__slide">

                    <!-- Single testimonial -->
                    <blockquote class="testimonial">
                      <!-- Text -->
                      <p class="testimonial__text">
                        {{ testimonial.text }}
                      </p>

                      <!-- Author -->
                      <cite class="testimonial__author">
                        <span class="testimonial__author__name">
                          {{ testimonial.author.name }}
                        </span>
                        <small class="testimonial__author__label">
                          {{ testimonial.author.label }}
                        </small>
                      </cite>
                    </blockquote>
                    <!-- END | Single testimonial -->

                  </li>
                </ul>
              </div>

              <!-- Pagination bullets -->
              <div class="glide__bullets" data-glide-el="controls[nav]">
                <button v-for="(testimonial, index) in module.content.testimonials" :key="testimonial.text"
                        class="glide__bullet" :data-glide-dir="'=' + index" />
              </div>
            </div>
          </div>
          <!-- END | Testimonials -->

        </div>
      </div>
    </section>
  `
})
export class TestimonialsModuleUi extends AbstractModuleUi<TestimonialsModule> {
  /**
   * Reference to the `.glide__track` element.
   */
  @Ref('glideTrackRef')
  private readonly glideTrackRef!: RefType<HTMLDivElement>

  /**
   * Glide instance.
   */
  private glide: typeof Glide | null = null

  /**
   * Height of the navbar (in px).
   */
  private navbarHeight: number = 0

  /**
   * Initialises the Glide.
   */
  private initGlide (): void {
    this.glide = new Glide('.testimonials-module__slider')

    this.glide.on('build.after', () => {
      this.updateGlideTrackHeight()
    })

    this.glide.on('run.after', () => {
      this.updateGlideTrackHeight()
      if (this.windowService.isPhone) this.scrollToTestimonial()
    })

    this.glide.mount()
  }

  /**
   * Reads the height of the navbar.
   */
  private readNavbarHeight (): void {
    const navbar = document.querySelector<HTMLElement>('.navbar')
    this.navbarHeight = navbar !== null ? navbar.getBoundingClientRect().height : 0
  }

  /**
   * Scrolls to the top of the testimonial.
   */
  private scrollToTestimonial (): void {
    if (typeof this.glideTrackRef === 'undefined') {
      return
    }

    const scrollOffset: number = window.pageYOffset
    const glideTrackPosition: number = this.glideTrackRef.getBoundingClientRect().top
    const buffer: number = 20

    const top = scrollOffset + glideTrackPosition - this.navbarHeight - buffer

    window.scrollTo({ top })
  }

  /**
   * Updates the height of the `.glide__track` element.
   */
  private updateGlideTrackHeight (): void {
    if (typeof this.glideTrackRef === 'undefined') {
      return
    }

    const activeSlide = document.querySelector<HTMLLIElement>('.glide__slide--active')
    const activeSlideHeight = activeSlide ? activeSlide.offsetHeight : 0

    const glideTrackHeight = this.glideTrackRef.offsetHeight

    if (activeSlideHeight !== glideTrackHeight) {
      this.glideTrackRef.style.height = `${activeSlideHeight}px`
    }
  }
}
